/**
 * useDocumentCreateStyleTemplate — orchestrates the visual style / template
 * choice for a single ``document_create`` action proposal.
 *
 * Sibling of ``useDocumentCreateDestination`` (same shape, same ergonomics):
 *   - Owns the controlled ``{ styleId?, templateSlug? }`` selection on behalf
 *     of the presentational ``<StyleTemplatePicker>``.
 *   - Fetches the style presets (``useStyles``) + uploaded OOXML templates
 *     (``listOoxmlTemplates``) via TanStack and adapts them to the picker's
 *     prop shapes — keeping ``@ando/ui`` data-free / Storybook-friendly.
 *   - Exposes ``buildModifications()`` returning the subset of execute
 *     ``modifications`` that carry the choice as the two content meta-keys
 *     ``_style_id`` / ``_ooxml_template_slug`` (mutually exclusive; template
 *     wins). The BE merges ``modifications`` into the action content, so this
 *     mirrors how ``save_destination`` flows today. Default selection (Ando)
 *     emits NOTHING, so the BE applies its own default and the audit-log diff
 *     stays minimal.
 *
 * Both lists degrade gracefully: a not-yet-deployed ``GET /api/v1/styles``
 * (404/501) yields an empty style list (handled in ``useStyles``); the
 * template list 404 simply renders the empty-state CTA.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getEnv, type BrandKit } from "@ando/core";
import type {
  BrandKitDefaultOption,
  OoxmlTemplateOption,
  StylePresetOption,
  StyleTemplateValue,
} from "@ando/ui";

import {
  listOoxmlTemplates,
  uploadOoxmlTemplate,
  type OoxmlTemplateItem,
  type StylePreset,
} from "@/features/sources/marp/api";
import { useStyles } from "@/features/sources/marp/useStyles";
import { useEffectiveBrandKit } from "@/features/settings/hooks/useBrandKits";
import {
  getLearnedStyleIds,
  useLearnStyleController,
} from "@/features/sources/marp/useLearnStyle";

/** Subset of execute ``modifications`` carrying the style/template choice. */
export interface DocumentCreateStyleTemplateModifications {
  readonly _style_id?: string;
  readonly _ooxml_template_slug?: string;
}

export interface UseDocumentCreateStyleTemplateResult {
  readonly value: StyleTemplateValue;
  readonly setValue: (next: StyleTemplateValue) => void;
  readonly styles: ReadonlyArray<StylePresetOption>;
  readonly templates: ReadonlyArray<OoxmlTemplateOption>;
  /**
   * The user's effective Brand Kit default (FIRST + pre-selected card). ``null``
   * when the caller has no kit at all (effective resolution never fails, so this
   * is only ``null`` defensively / on a not-yet-deployed BE).
   */
  readonly brandKit: BrandKitDefaultOption | null;
  readonly brandKitLoading: boolean;
  readonly stylesLoading: boolean;
  readonly templatesLoading: boolean;
  /** Learn a new style from a dropped reference file (validated + mutated). */
  readonly onLearnStyle: (file: File) => void;
  readonly learnStylePending: boolean;
  readonly learnStyleError: string | null;
  /** Upload a brand template inline from the gallery (auto-selected on success). */
  readonly onUploadTemplate: (file: File) => void;
  readonly uploadTemplatePending: boolean;
  /** Build the style/template subset to spread into ``executeAction``'s body. */
  readonly buildModifications: () => DocumentCreateStyleTemplateModifications;
}

/** Map the BE style wire (``RRGGBB`` hex) → the picker's camelCase option. */
function adaptStyle(s: StylePreset, learnedIds: ReadonlySet<string>): StylePresetOption {
  return {
    id: s.id,
    name: s.name,
    category: s.category,
    isDark: s.is_dark,
    accent: s.accent,
    ink: s.ink,
    surface: s.surface,
    isLearned: learnedIds.has(s.id),
    // Rendered mini-cover (PIL, brand fonts) — learned styles have no preset
    // thumbnail, they keep the palette swatch.
    thumbnailUrl: learnedIds.has(s.id)
      ? undefined
      : `${getEnv().apiUrl}/styles/${encodeURIComponent(s.id)}/thumbnail`,
  };
}

/** Map the BE OOXML template wire → the picker's option shape. The thumbnail
 * is a cookie-authed `<img>` URL (the BE renders + caches slide 1). */
function adaptTemplate(t: OoxmlTemplateItem): OoxmlTemplateOption {
  const thumbnailUrl =
    t.has_thumbnail !== false
      ? `${getEnv().apiUrl}/templates/ooxml/${encodeURIComponent(t.slug)}/thumbnail`
      : undefined;
  return {
    slug: t.slug,
    name: t.name || undefined,
    description: t.description || undefined,
    scope: t.scope,
    documentType: t.document_type,
    isBuiltin: t.is_builtin ?? false,
    testOnly: t.test_only ?? false,
    // The AI tokenizer marks a merge-ready customer deck with mode='tokenized'.
    mergeReady: t.mode === "tokenized",
    thumbnailUrl,
  };
}

/**
 * Map the effective Brand Kit → the picker's default-card option. The kit's
 * palette (``spec.style``) drives a lightweight preview; ``ooxml_template_key``
 * tells the card whether the kit merges a real template on top or only themes
 * the generated file.
 */
function adaptBrandKit(kit: BrandKit): BrandKitDefaultOption {
  const s = kit.spec.style;
  // The BE "category" is warm/cool/neutral/dark; only "dark" flips the preview.
  const isDark = s.category === "dark";
  return {
    name: kit.name,
    hasTemplate: Boolean(kit.spec.ooxml_template_key),
    accent: s.accent,
    ink: s.ink,
    surface: s.surface,
    isDark,
    // Rendered mini-cover of the effective kit — the Style tab's DEFAULT row
    // shows the customer's real palette/typography (cookie-authed <img>).
    thumbnailUrl: `${getEnv().apiUrl}/brand-kits/_effective/thumbnail`,
  };
}

/** Derive a BE-valid slug (``^[a-z][a-z0-9-]{2,40}$``) from an upload filename. */
function slugFromFilename(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "").toLowerCase();
  let slug = base.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (!/^[a-z]/.test(slug)) slug = `t-${slug}`;
  slug = slug.slice(0, 40);
  return slug.length >= 3 ? slug : `tpl-${slug}`;
}

export function useDocumentCreateStyleTemplate(
  /**
   * Gate ALL three fetches (styles / brand-kit / templates). Pass the
   * draft-create condition so non-create doc opens don't fire needless I/O —
   * ``ArtifactPane`` calls this unconditionally (rules-of-hooks) for EVERY doc.
   */
  enabled: boolean = true,
): UseDocumentCreateStyleTemplateResult {
  const [value, setValue] = React.useState<StyleTemplateValue>({});

  const stylesQuery = useStyles({ enabled });
  // The effective Brand Kit (user → org → builtin Ando) surfaced as the FIRST,
  // pre-selected card. Shares the ``["brand-kits","effective"]`` cache with the
  // settings surface (no extra fetch when the user has already opened it).
  const brandKitQuery = useEffectiveBrandKit({ enabled });
  const templatesQuery = useQuery<{ readonly templates: readonly OoxmlTemplateItem[] }>({
    queryKey: ["ooxml-templates"],
    queryFn: listOoxmlTemplates,
    staleTime: 60_000,
    retry: 1,
    enabled,
  });

  // Auto-select the freshly-learned style so the user's just-uploaded
  // reference becomes the active pick (clearing any template choice).
  const learn = useLearnStyleController({
    onLearned: (style) => setValue({ styleId: style.id, templateSlug: undefined }),
  });

  const styles = React.useMemo<ReadonlyArray<StylePresetOption>>(() => {
    const learnedIds = getLearnedStyleIds();
    return (stylesQuery.data ?? []).map((s) => adaptStyle(s, learnedIds));
    // ``learn.pending`` is a dep so the learned-id badge re-resolves once a
    // newly-learned style lands in the (invalidated) styles query.
  }, [stylesQuery.data, learn.pending]);

  const templates = React.useMemo<ReadonlyArray<OoxmlTemplateOption>>(
    () => (templatesQuery.data?.templates ?? []).map(adaptTemplate),
    [templatesQuery.data],
  );

  const brandKit = React.useMemo<BrandKitDefaultOption | null>(
    () => (brandKitQuery.data ? adaptBrandKit(brandKitQuery.data) : null),
    [brandKitQuery.data],
  );

  // Inline template upload from the gallery (parent owns the mutation; on
  // success we invalidate the list + auto-select the new template).
  const queryClient = useQueryClient();
  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      uploadOoxmlTemplate(file, { slug: slugFromFilename(file.name), scope: "user" }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ["ooxml-templates"] });
      if (res?.slug) setValue({ styleId: undefined, templateSlug: res.slug });
    },
  });
  const onUploadTemplate = React.useCallback(
    (file: File) => uploadMutation.mutate(file),
    [uploadMutation],
  );

  const buildModifications =
    React.useCallback((): DocumentCreateStyleTemplateModifications => {
      // Template wins (it's a fixed-fill file); never emit both.
      if (value.templateSlug) {
        return { _ooxml_template_slug: value.templateSlug };
      }
      // An EXPLICIT style pick wins and is emitted as-is. The DEFAULT (user
      // left it untouched) emits NOTHING → the BE applies the user's BRAND KIT
      // (the agent never sets ``style_id``; the visual style is the user's
      // post-gen choice). NOTE: emitting ``_style_id:"ando"`` here was the real
      // source of the "deck forza lo stile ando / troppo nero" bug — it
      // overrode the Brand Kit on every default deck. The LLM can no longer
      // sneak a style into the proposal (the BE drops it in
      // ``propose_document_create``), so the default safely defers to the kit.
      if (value.styleId) {
        return { _style_id: value.styleId };
      }
      return {};
    }, [value]);

  return {
    value,
    setValue,
    styles,
    templates,
    brandKit,
    brandKitLoading: brandKitQuery.isLoading,
    stylesLoading: stylesQuery.isLoading,
    templatesLoading: templatesQuery.isLoading,
    onLearnStyle: learn.learn,
    learnStylePending: learn.pending,
    learnStyleError: learn.error,
    onUploadTemplate,
    uploadTemplatePending: uploadMutation.isPending,
    buildModifications,
  };
}

/**
 * Map the hook result → the shared ``StyleTemplatePicker`` prop shape — the
 * SINGLE source of truth for the picker spread, reused by BOTH the
 * FocusedActionView card and the editor DraftActionBar so the two surfaces
 * can't drift. ``opts`` carry the only per-surface bits (the ``disabled``
 * source + the optional templates-empty-state settings link). The returned
 * object satisfies ``StyleTemplatePickerProps`` (editor) and is assignable to
 * the card's ``DocumentCreateStyleTemplateInteractive`` (a structural subset).
 */
export function toStyleTemplatePickerProps(
  s: UseDocumentCreateStyleTemplateResult,
  opts?: {
    readonly disabled?: boolean;
    readonly onOpenTemplateSettings?: () => void;
  },
) {
  return {
    value: s.value,
    onChange: s.setValue,
    styles: s.styles,
    templates: s.templates,
    brandKit: s.brandKit,
    brandKitLoading: s.brandKitLoading,
    stylesLoading: s.stylesLoading,
    templatesLoading: s.templatesLoading,
    onLearnStyle: s.onLearnStyle,
    learnStylePending: s.learnStylePending,
    learnStyleError: s.learnStyleError,
    onUploadTemplate: s.onUploadTemplate,
    uploadTemplatePending: s.uploadTemplatePending,
    onOpenTemplateSettings: opts?.onOpenTemplateSettings,
    disabled: opts?.disabled,
  };
}
